import { FC } from 'react';
import DistributionBars from '../../../shared/charts/DistributionBars';
import Typhography from '../../../shared/Typhography/Typhography';
import { ITrainingMixPlayerDensity } from '../../../models/allenamenti';
import { formatMinutes } from './analysis.constants';
import { usePlayerGroupPanel } from './usePlayerGroupPanel';
import styles from './PlayerGroupPanel.module.scss';

interface IPlayerGroupPanelProps {
  /** `null` = nessun blocco di gioco nel periodo. */
  data: ITrainingMixPlayerDensity | null;
}

/**
 * Pannello «Dimensione dei gruppi» (F2): distribuzione dei minuti pianificati
 * per numero di giocatori in campo (Individuale/Piccoli/Medi/Grandi), con il
 * m² medio di campo e le modalità (continuo/intermittente/analitico) per
 * ciascun gruppo, più la modalità complessiva sull'intero periodo.
 *
 * Da non confondere con «Densità di gioco» (m²/giocatore, GameDensityPanel):
 * lì il criterio è lo spazio per atleta, qui è la dimensione del gruppo.
 */
const PlayerGroupPanel: FC<IPlayerGroupPanelProps> = ({ data }) => {
  const { groupBars, groupDetails, totalFlowBars } = usePlayerGroupPanel(data);

  return (
    <div className={styles.card}>
      <Typhography type="h3" size="bold-14" className={styles.title}>
        Dimensione dei gruppi
      </Typhography>
      <Typhography size="regular-12" className={styles.subtitle}>
        Distribuzione del lavoro per numero di giocatori in campo.
      </Typhography>

      {!data ? (
        <p className={styles.empty}>Nessun blocco di gioco nel periodo.</p>
      ) : data.computedBlocks === 0 ? (
        <p className={styles.empty}>
          Blocchi di gioco presenti ({data.eligibleBlocks}) ma senza numero di
          giocatori: nessun calcolo possibile.
        </p>
      ) : (
        <>
          <DistributionBars
            data={groupBars}
            valueFormatter={formatMinutes}
            labelMode="value-percent"
          />

          <div className={styles.groupGrid}>
            {groupDetails.map((group) => (
              <div key={group.id} className={styles.groupCard}>
                <div className={styles.groupHeader}>
                  {/* Colore runtime dal gruppo @leopart/calc, non un token statico. */}
                  <span className={styles.dot} style={{ background: group.color }} />
                  <span className={styles.groupLabel}>{group.label}</span>
                  <span className={styles.groupMinutes}>
                    {formatMinutes(group.minutes)}
                    {/* Stessa precisione della barra overview (valore dal backend, 1 decimale). */}
                    {group.pct != null && ` · ${group.pct}%`}
                  </span>
                </div>
                <p className={styles.groupMeta}>
                  {group.avgAreaM2 != null && (
                    <>
                      m² medio: <b>{Math.round(group.avgAreaM2)} m²</b>
                      {' · '}
                    </>
                  )}
                  {group.blocks} {group.blocks === 1 ? 'blocco' : 'blocchi'}
                </p>
                <DistributionBars data={group.flowBars} valueFormatter={formatMinutes} />
              </div>
            ))}
          </div>

          <Typhography size="bold-12" className={styles.miniTitle}>
            Modalità complessiva (tutti i gruppi)
          </Typhography>
          <DistributionBars
            data={totalFlowBars}
            valueFormatter={formatMinutes}
            labelMode="value-percent"
          />

          {data.excludedBlocks > 0 && (
            <p className={styles.meta}>
              <b>{data.excludedBlocks}</b>{' '}
              {data.excludedBlocks === 1
                ? 'blocco senza numero di giocatori (escluso dal calcolo)'
                : 'blocchi senza numero di giocatori (esclusi dal calcolo)'}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default PlayerGroupPanel;
